import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, ArrowUpDown, Search, Trash2, AlertTriangle, Loader2, RefreshCw } from "lucide-react";
import InfiniteSentinel from "./InfiniteSentinel";
import LiveCount from "./LiveCount";
import JumpRail from "./JumpRail";
import AlphaRail from "./AlphaRail";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

const errMsg = (e) => formatApiError(e?.response?.data?.detail);

/**
 * Get a value from a nested object by dotted path.
 * Returns undefined if any segment is missing.
 */
export function getPath(obj, path) {
  if (!path) return undefined;
  return path.split(".").reduce((acc, k) => (acc == null ? undefined : acc[k]), obj);
}

export function formatMoney(v) {
  if (v === null || v === undefined || v === "") return "—";
  // Pre-formatted currency strings (e.g. from BigCommerce): return as-is
  if (typeof v === "string" && /^\s*[£$€¥]/.test(v)) return v.trim();
  const n = Number(String(v).replace(/[£$€¥,]/g, ""));
  if (Number.isNaN(n)) return String(v);
  return `£${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Format any Mongo date-ish input into "Wed, 08 Jul 2026". Returns "—" if unparseable. */
export function formatShortDate(v) {
  if (!v) return "—";
  const raw = String(v).trim();
  if (!raw) return "—";
  // Try parsing as-is first (handles RFC 2822, ISO 8601, etc.)
  let d = new Date(raw);
  // Fallback: only for "YYYY-MM-DD HH:MM:SS" shape which Safari won't accept without the "T"
  if (isNaN(d.getTime()) && /^\d{4}-\d{2}-\d{2} \d{2}:/.test(raw)) {
    d = new Date(raw.replace(" ", "T"));
  }
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
}

const PAGE_SIZES = [10, 25, 50, 100];

export default function AdminCollectionTable({
  collectionName,
  title,
  subtitle,
  columns,
  emptyKeywordRequired = "EMPTY",
  alphaField = null,   // when set, show an A–Z rail that prefix-filters this field
  filters = [],        // [{ key, label, param, options:[{value,label}] }] → dropdown filters
}) {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [letter, setLetter] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState("");
  const [pendingSearch, setPendingSearch] = useState("");
  const [filterValues, setFilterValues] = useState({}); // { param: value }
  const [limit, setLimit] = useState(50);
  const [sort, setSort] = useState(null); // {path, order}
  const [deleteTarget, setDeleteTarget] = useState(null); // doc
  const [emptyDialogOpen, setEmptyDialogOpen] = useState(false);
  const [emptyConfirmText, setEmptyConfirmText] = useState("");
  const [actionBusy, setActionBusy] = useState(false);
  const pageRef = useRef(1);
  const loadingRef = useRef(false);
  const genRef = useRef(0);
  const [jumpedPage, setJumpedPage] = useState(0);

  const fetchAt = useCallback(async (page, append) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    if (append) setLoadingMore(true); else setLoading(true);
    const gen = genRef.current;
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("limit", String(limit));
      if (search.trim()) params.set("search", search.trim());
      if (letter && alphaField) {
        params.set("starts_with", letter);
        params.set("sort", alphaField);
        params.set("order", "asc");
      } else if (sort) {
        params.set("sort", sort.path);
        params.set("order", sort.order);
      }
      for (const [param, val] of Object.entries(filterValues)) {
        if (val !== "" && val !== null && val !== undefined) params.set(param, val);
      }
      const res = await api.get(`/admin/collections/${collectionName}?${params.toString()}`);
      if (gen !== genRef.current) return;
      const batch = res.data.items || [];
      const total = res.data.total || 0;
      setData((prev) => ({ items: append ? [...prev.items, ...batch] : batch, total }));
      pageRef.current = page;
      setHasMore(page * limit < total);
    } catch (e) {
      if (gen === genRef.current) toast.error("Failed to load", { description: errMsg(e) });
    } finally {
      if (gen === genRef.current) { loadingRef.current = false; setLoading(false); setLoadingMore(false); }
    }
  }, [collectionName, limit, search, sort, letter, alphaField, filterValues]);

  const load = useCallback(() => {
    genRef.current += 1;
    pageRef.current = 1;
    loadingRef.current = false;
    setHasMore(true);
    setJumpedPage(0);
    fetchAt(1, false);
  }, [fetchAt]);

  const loadMore = useCallback(() => {
    if (!loadingRef.current && hasMore) fetchAt(pageRef.current + 1, true);
  }, [hasMore, fetchAt]);

  const jumpTo = useCallback((offset) => {
    const targetPage = Math.floor(offset / limit) + 1;
    genRef.current += 1;
    pageRef.current = targetPage;
    loadingRef.current = false;
    setHasMore(true);
    setJumpedPage(targetPage);
    fetchAt(targetPage, false);
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [fetchAt, limit]);

  const resetJump = useCallback(() => {
    setJumpedPage(0);
    load();
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [load]);

  useEffect(() => { load(); }, [load]);

  const toggleSort = (col) => {
    if (!col.sortable) return;
    setLetter(null);
    setSort((prev) => {
      if (!prev || prev.path !== col.sortPath) return { path: col.sortPath, order: "asc" };
      if (prev.order === "asc") return { path: col.sortPath, order: "desc" };
      return null; // 3rd click clears
    });
  };

  const sortIcon = (col) => {
    if (!col.sortable) return null;
    const active = sort && sort.path === col.sortPath;
    if (!active) return <ArrowUpDown className="w-3.5 h-3.5 opacity-40" />;
    return sort.order === "asc" ? <ArrowUp className="w-3.5 h-3.5 text-orange-400" /> : <ArrowDown className="w-3.5 h-3.5 text-orange-400" />;
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    setLetter(null);
    setSearch(pendingSearch);
  };

  const confirmDeleteRow = async () => {
    if (!deleteTarget) return;
    setActionBusy(true);
    try {
      await api.delete(`/admin/collections/${collectionName}/${encodeURIComponent(deleteTarget._id)}`);
      toast.success("Row deleted");
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error("Delete failed", { description: errMsg(e) });
    } finally {
      setActionBusy(false);
    }
  };

  const confirmEmptyCollection = async () => {
    setActionBusy(true);
    try {
      const res = await api.delete(`/admin/collections/${collectionName}`);
      toast.success(`Deleted ${res.data.deleted} document${res.data.deleted === 1 ? "" : "s"}`);
      setEmptyDialogOpen(false);
      setEmptyConfirmText("");
      await load();
    } catch (e) {
      toast.error("Empty failed", { description: errMsg(e) });
    } finally {
      setActionBusy(false);
    }
  };

  const start = data.total === 0 ? 0 : 1;
  const end = data.items.length;

  return (
    <div className="fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight flex items-center gap-3" data-testid={`${collectionName}-mgmt-title`}>
            {title}
            <LiveCount shown={data.items.length} total={data.total} testId={`${collectionName}-live-count`} />
          </h1>
          {subtitle && <p className="text-slate-400 mt-2">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium border border-white/10 text-slate-200 hover:bg-white/5 disabled:opacity-50"
            data-testid={`${collectionName}-refresh-button`}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setEmptyDialogOpen(true)}
            disabled={data.total === 0}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium border border-red-500/30 text-red-300 hover:bg-red-500/10 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`${collectionName}-empty-button`}
          >
            <Trash2 className="w-4 h-4" />
            Empty Collection
          </button>
        </div>
      </div>

      <div className="card-dark p-4 border border-white/5 mb-4">
        <form onSubmit={onSearchSubmit} className="flex items-center gap-2">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
            <input
              type="text"
              placeholder="Search..."
              value={pendingSearch}
              onChange={(e) => setPendingSearch(e.target.value)}
              className="input-dark py-2 text-sm w-full"
              style={{ paddingLeft: "2.25rem" }}
              data-testid={`${collectionName}-search-input`}
            />
          </div>
          <button type="submit" className="btn-coral text-sm py-2" data-testid={`${collectionName}-search-submit`}>Search</button>
          {filters.map((f) => (
            <select
              key={f.key}
              value={filterValues[f.param] ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                setLetter(null);
                setFilterValues((prev) => {
                  const next = { ...prev };
                  if (v === "") delete next[f.param];
                  else next[f.param] = v;
                  return next;
                });
              }}
              className="input-dark py-2 text-sm"
              data-testid={`${collectionName}-filter-${f.key}`}
            >
              <option value="">All {f.label}</option>
              {f.options.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          ))}
          {(search || Object.keys(filterValues).length > 0) && (
            <button
              type="button"
              onClick={() => { setPendingSearch(""); setSearch(""); setLetter(null); setFilterValues({}); }}
              className="text-xs text-slate-400 hover:text-slate-200"
              data-testid={`${collectionName}-search-clear`}
            >Clear</button>
          )}
          <div className="ml-auto text-xs text-slate-400" data-testid={`${collectionName}-count`}>
            {loading ? "Loading..." : `${start}-${end} of ${data.total}`}
          </div>
        </form>
      </div>

      <div className="card-dark border border-white/5 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] border-b border-white/5">
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    onClick={() => toggleSort(col)}
                    className={`text-left px-4 py-3 text-[11px] uppercase tracking-wider text-slate-400 font-medium whitespace-nowrap ${col.sortable ? "cursor-pointer select-none hover:text-slate-200" : ""}`}
                    data-testid={`${collectionName}-col-${col.key}`}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {col.label}
                      {sortIcon(col)}
                    </span>
                  </th>
                ))}
                <th className="text-right px-4 py-3 text-[11px] uppercase tracking-wider text-slate-400 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && data.items.length === 0 && (
                <tr><td colSpan={columns.length + 1} className="px-4 py-10 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading...</td></tr>
              )}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={columns.length + 1} className="px-4 py-10 text-center text-slate-500" data-testid={`${collectionName}-empty-state`}>No records found.</td></tr>
              )}
              {data.items.map((row) => (
                <tr key={row._id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`${collectionName}-row-${row._id}`}>
                  {columns.map((col) => {
                    const rawVal = getPath(row, col.path);
                    const rendered = col.formatter ? col.formatter(rawVal, row) : (rawVal ?? "—");
                    return (
                      <td key={col.key} className="px-4 py-3 text-slate-200 whitespace-nowrap max-w-xs truncate" title={typeof rendered === "string" ? rendered : undefined}>
                        {rendered === "" || rendered === undefined || rendered === null ? "—" : rendered}
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => setDeleteTarget(row)}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-red-300 hover:bg-red-500/10 border border-red-500/20"
                      data-testid={`${collectionName}-row-delete-${row._id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-white/5 text-xs">
          <div className="flex items-center gap-2 text-slate-400">
            Rows per fetch
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="input-dark py-1 text-xs"
              data-testid={`${collectionName}-page-size`}
            >
              {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <span className="text-slate-500" data-testid={`${collectionName}-loaded-count`}>
            {data.items.length} of {data.total} loaded
          </span>
        </div>

        {data.items.length > 0 && (
          <InfiniteSentinel
            onReach={loadMore}
            hasMore={hasMore}
            loading={loadingMore}
            count={data.items.length}
            total={data.total}
            testId={`${collectionName}-sentinel`}
          />
        )}
      </div>

      {/* Delete row confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !actionBusy && !v && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#0F1420] border-white/10 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Delete this record?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This will permanently remove the record with ID <span className="font-mono text-slate-200 break-all">{deleteTarget?._id}</span> from the <span className="text-slate-200">{collectionName}</span> collection.
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border border-white/10 text-slate-300 hover:bg-white/5" data-testid={`${collectionName}-delete-cancel`}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteRow}
              disabled={actionBusy}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid={`${collectionName}-delete-confirm`}
            >
              {actionBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Empty collection confirm */}
      <AlertDialog open={emptyDialogOpen} onOpenChange={(v) => !actionBusy && setEmptyDialogOpen(v)}>
        <AlertDialogContent className="bg-[#0F1420] border-white/10 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Empty entire {collectionName} collection?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This will permanently delete <span className="text-slate-200 font-bold">{data.total}</span> document{data.total === 1 ? "" : "s"} from <span className="text-slate-200">{collectionName}</span>.
              This action cannot be undone.
              <br /><br />
              Type <span className="font-mono text-red-300">{emptyKeywordRequired}</span> to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <input
            autoFocus
            type="text"
            value={emptyConfirmText}
            onChange={(e) => setEmptyConfirmText(e.target.value)}
            placeholder={emptyKeywordRequired}
            className="input-dark py-2 text-sm my-1"
            data-testid={`${collectionName}-empty-confirm-input`}
          />
          <AlertDialogFooter>
            <AlertDialogCancel
              className="bg-transparent border border-white/10 text-slate-300 hover:bg-white/5"
              onClick={() => setEmptyConfirmText("")}
              data-testid={`${collectionName}-empty-cancel`}
            >Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmEmptyCollection}
              disabled={actionBusy || emptyConfirmText !== emptyKeywordRequired}
              className="bg-red-500 hover:bg-red-600 text-white disabled:opacity-40"
              data-testid={`${collectionName}-empty-confirm`}
            >
              {actionBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Delete all ${data.total}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {alphaField ? (
        <AlphaRail active={letter} onPick={setLetter} testId={`${collectionName}-alpha-rail`} />
      ) : (
        <JumpRail
          total={data.total}
          atOffset={jumpedPage > 0 ? (jumpedPage - 1) * limit : 0}
          onJump={jumpTo}
          onReset={resetJump}
          testId={`${collectionName}-jump-rail`}
        />
      )}
    </div>
  );
}
